// Central Hub Integration for AdCanvas
// Configuration and utilities for connecting to the Central Hub.
//
// SECURITY: hub access tokens are verified cryptographically against the hub's
// published JWKS (ES256) via `jose`. We NEVER trust claims from an unverified
// `atob` decode — the spine token is forgeable if its signature isn't checked.
// The claims are ADVISORY: AdCanvas still applies its own local access rules.

import { jwtVerify, createRemoteJWKSet } from 'jose';

export const HUB_CONFIG = {
  // Hub front-end (login UI / app switcher)
  hubUrl: import.meta.env.VITE_HUB_URL || 'https://hub.xyzstrategy.co',
  hubApiUrl: import.meta.env.VITE_HUB_API_URL || 'https://hub.xyzstrategy.co/api',

  // The hub's Supabase project (the OIDC issuer) — used to verify token signatures.
  hubSupabaseUrl: import.meta.env.VITE_HUB_SUPABASE_URL || 'https://scqlegrrdaxdrmiyvsax.supabase.co',

  // This platform's identifier (matches the hub `apps.slug`).
  platformId: 'adcanvas' as const,

  // Callback URL for this platform
  callbackUrl: `${window.location.origin}/auth/hub-callback`,
};

// Issuer + JWKS endpoint. The JWKS URL is overridable via env in case the
// project exposes it at a non-default path (confirm via the OIDC well-known doc).
const HUB_ISSUER = `${HUB_CONFIG.hubSupabaseUrl}/auth/v1`;
const HUB_JWKS_URL =
  import.meta.env.VITE_HUB_JWKS_URL ||
  `${HUB_CONFIG.hubSupabaseUrl}/auth/v1/.well-known/jwks.json`;

// Remote JWKS with built-in caching + cooldown (jose handles key rotation).
const hubJwks = createRemoteJWKSet(new URL(HUB_JWKS_URL));

export type PlatformId = 'realnumbers' | 'adcanvas' | 'xyzhq';
export type UserRole = 'owner' | 'admin' | 'manager' | 'contributor' | 'viewer';

export interface HubPlatformAccess {
  enabled: boolean;
  organization_id: string;
  organization_name: string;
  role: UserRole;
  app_id?: string;
}

// Hub JWT claims (advisory). `platforms` is keyed by app slug.
export interface HubJWTClaims {
  sub: string;
  email?: string;
  platforms?: Record<string, HubPlatformAccess>;
  is_super_admin?: boolean;
  is_operator?: boolean;
  operator_level?: 'admin' | 'standard' | 'analyst' | 'read_only' | null;
  hub_status?: 'active' | 'suspended' | 'offboarded';
  session_id?: string;
  exp: number;
  iat: number;
}

// Token storage keys
const TOKEN_KEY = 'hub_access_token';
const REFRESH_TOKEN_KEY = 'hub_refresh_token';

/**
 * Redirect to the hub login page.
 */
export function redirectToHubLogin(): void {
  const params = new URLSearchParams({
    platform: HUB_CONFIG.platformId,
    redirect: HUB_CONFIG.callbackUrl,
  });
  window.location.href = `${HUB_CONFIG.hubUrl}/login?${params.toString()}`;
}

/**
 * Cryptographically verify a hub access token against the hub JWKS.
 * Returns the verified claims, or null if the token is missing/invalid/expired.
 * This is the ONLY trusted path to read hub claims.
 */
export async function verifyHubToken(token?: string | null): Promise<HubJWTClaims | null> {
  const candidate = token ?? getAccessToken();
  if (!candidate) return null;
  try {
    const { payload } = await jwtVerify(candidate, hubJwks, { issuer: HUB_ISSUER });
    return payload as unknown as HubJWTClaims;
  } catch (error) {
    console.error('Hub token verification failed:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Legacy custom-token-server flow (HS256 `auth-token` edge function).
// Slated for retirement once the hub OIDC issuer is live (adoption plan §5b/§5c).
// Kept temporarily so the existing hub-callback wiring keeps compiling.
// ---------------------------------------------------------------------------

/** @deprecated legacy auth-token server; replaced by hub OIDC. */
export async function exchangeCodeForTokens(code: string): Promise<{
  access_token: string;
  refresh_token: string;
  expires_in: number;
} | null> {
  try {
    const response = await fetch(`${HUB_CONFIG.hubApiUrl}/auth-token/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: HUB_CONFIG.callbackUrl }),
    });
    if (!response.ok) {
      console.error('Token exchange failed:', await response.json().catch(() => ({})));
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Token exchange error:', error);
    return null;
  }
}

/** @deprecated legacy auth-token server; replaced by hub OIDC. */
export async function refreshAccessToken(): Promise<{
  access_token: string;
  refresh_token: string;
  expires_in: number;
} | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await fetch(`${HUB_CONFIG.hubApiUrl}/auth-token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) {
      clearTokens();
      return null;
    }
    const tokens = await response.json();
    storeTokens(tokens.access_token, tokens.refresh_token);
    return tokens;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
}

/** @deprecated legacy auth-token server; replaced by hub OIDC. */
export async function revokeTokens(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await fetch(`${HUB_CONFIG.hubApiUrl}/auth-token/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (error) {
      console.error('Token revocation error:', error);
    }
  }
  clearTokens();
}

/** Store tokens in localStorage. */
export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Whether the stored token is present AND cryptographically valid (signature +
 * issuer + not-expired). Async because verification fetches/caches the JWKS.
 */
export async function hasValidToken(): Promise<boolean> {
  const claims = await verifyHubToken();
  if (!claims) return false;
  return claims.exp > Date.now() / 1000 + 60;
}

/**
 * Get this platform's access from the VERIFIED token. Returns null unless the
 * signature checks out and the platform is enabled for the user.
 */
export async function getPlatformAccess(): Promise<HubPlatformAccess | null> {
  const claims = await verifyHubToken();
  if (!claims) return null;

  const platformAccess = claims.platforms?.[HUB_CONFIG.platformId];
  if (!platformAccess || !platformAccess.enabled) return null;

  return platformAccess;
}

/** Whether the current URL is the hub callback path. */
export function isHubCallback(): boolean {
  return window.location.pathname === '/auth/hub-callback';
}

/** Get the auth code from the URL. */
export function getAuthCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}
